import * as Sentry from '@sentry/react';

/**
 * Boîte noire des sockets ÉCRAN — diagnostic temporaire.
 *
 * Pourquoi : les logs serveur montrent qu'un écran ouvre un nouveau socket ~9 fois
 * par soirée (médiane, jusqu'à 20), et que dans 100 % des cas l'ANCIEN socket était
 * encore vivant — le serveur ne voit donc aucune coupure réseau. C'est l'appli qui
 * recrée sa connexion, pas le wifi du bar. Mesuré le 15/09/2026 : 158 évictions
 * écran contre 68 manettes, dont 81 en mode `anim` où aucun rechargement n'est
 * prévu (le retour au menu ne démonte que `ScreenForInstance`, socket vivant).
 *
 * Ce que les données existantes ne peuvent PAS dire, et pourquoi ce module existe :
 * - Les logs Railway ne voient que la conséquence (l'éviction), jamais la cause.
 * - `health.endReason` de la boîte noire de session ne convient pas : le
 *   `FirestoreScreenDataCollector` est instancié par JEU (`ScreenForInstance`), donc
 *   ses 67 `unmount` du 15/09 sont des retours au menu — normaux, sans nouveau socket.
 *
 * On veut trancher entre deux causes, qui n'ont pas du tout le même correctif :
 *   - `ordinal > 1`            -> `ScreenApp` est REMONTÉ dans la même page.
 *   - `ordinal === 1` + `reload` -> la page est RECHARGÉE (chercher qui la recharge).
 *
 * Pour « chercher qui la recharge » : chaque rechargement VOULU passe par
 * `reloadScreen(raison)`, qui note la raison juste avant (sessionStorage survit au
 * reload). La sonde la relit au démarrage : seule la rotation des jeux, rechargement
 * normal, est tue. Tout le reste remonte AVEC sa raison — `raison=inconnue` = rien
 * dans l'appli n'a demandé ce rechargement (kiosque, TV, navigateur).
 * ⚠️ 27/09/2026 : les aéroports free-play (MRS/ORY/BOD) rechargent ~40 fois/jour
 * alors que la rotation y est coupée — c'est précisément ce qu'on cherche à expliquer.
 *
 * À RETIRER (ou passer en breadcrumb seul) dès que la question est tranchée.
 */

/** Pourquoi l'appli recharge elle-même la page. */
export type ReloadReason =
    | 'rotation' // useRandomGameReload : changement de jeu prévu (auto hors free-play)
    | 'session-fermee' // ScreenSessionRecovery : le serveur a fermé la session
    | 'bouton-reessayer' // ScreenConnectLoader : connexion au serveur impossible
    | 'page-erreur'; // écran d'erreur global (App.tsx)

/** Seule raison qui n'est pas une anomalie : la rotation normale des jeux. */
const EXPECTED_REASONS: ReadonlySet<string> = new Set<ReloadReason>(['rotation']);

const REASON_KEY = 'ub:screenReloadReason';

/** Une raison plus vieille est périmée (onglet rouvert bien plus tard, reload raté). */
const REASON_MAX_AGE_MS = 60_000;

/** Sockets écran créés depuis le chargement de cette page. Remis à 0 par un reload. */
let created = 0;

/** Plafond par chargement de page : un remontage en boucle ne doit pas inonder Sentry. */
const MAX_REPORTS_PER_PAGE = 3;

/** 'navigate' (1re visite), 'reload' (F5 / location.reload), 'back_forward'. */
function navigationType(): string {
    try {
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        return nav?.type ?? 'inconnu';
    } catch {
        return 'inconnu';
    }
}

/** Note la raison puis recharge. Remplace tout `window.location.reload()` voulu. */
export function reloadScreen(reason: ReloadReason) {
    try {
        sessionStorage.setItem(REASON_KEY, JSON.stringify({ reason, at: Date.now() }));
    } catch {
        /* Stockage refusé : on recharge quand même, la raison sera « inconnue ». */
    }
    window.location.reload();
}

/** Lit ET efface la raison notée avant ce chargement de page (lecture unique). */
function consumeReloadReason(): string | undefined {
    try {
        const raw = sessionStorage.getItem(REASON_KEY);
        if (!raw) return undefined;
        sessionStorage.removeItem(REASON_KEY);
        const { reason, at } = JSON.parse(raw) as { reason?: string; at?: number };
        return typeof at === 'number' && Date.now() - at <= REASON_MAX_AGE_MS ? reason : undefined;
    } catch {
        return undefined;
    }
}

/**
 * À appeler juste avant `.init()` du socket écran. Ne throw jamais : ce chemin
 * s'exécute au démarrage de la TV, une sonde ne doit pas empêcher l'écran de vivre.
 */
export function auditScreenSocketCreation(clientID: string, mode?: string) {
    try {
        created += 1;
        // Uniquement au 1er socket de la page : c'est lui qui suit le rechargement.
        const reloadReason = created === 1 ? consumeReloadReason() : undefined;
        const data = {
            /** 1 = premier socket de cette page. >1 = ScreenApp remonté SANS rechargement. */
            ordinal: created,
            navigation: navigationType(),
            /** Secondes depuis le chargement de la page : distingue un démarrage d'un remontage tardif. */
            sincePageLoadS: Math.round(performance.now() / 1000),
            clientID,
            mode,
            /** Raison notée par reloadScreen() avant ce chargement ; absente = inconnue. */
            reloadReason,
            path: window.location.pathname,
        };

        // Breadcrumb toujours : gratuit, et il accompagnera toute erreur ultérieure.
        Sentry.addBreadcrumb({ category: 'screen-socket', message: 'Socket écran créé', level: 'info', data });

        // Un premier socket sur une page ouverte normalement est le démarrage attendu
        // de la TV : aucun intérêt, et ça diluerait le signal. On ne remonte que les
        // DEUX anomalies cherchées — remontage dans la page, ou page rechargée — pour
        // que tout événement dans Sentry soit, par construction, une anomalie.
        // Exception : un rechargement dont l'appli a donné une raison attendue
        // (rotation des jeux) n'est pas une anomalie.
        const pageRechargee = data.navigation !== 'navigate' && !EXPECTED_REASONS.has(reloadReason ?? '');
        const anomalie = created > 1 || pageRechargee;
        if (anomalie && created <= MAX_REPORTS_PER_PAGE) {
            Sentry.captureMessage(
                `Socket écran créé (#${created}, navigation=${data.navigation}, raison=${
                    reloadReason ?? 'inconnue'
                }, +${data.sincePageLoadS}s)`,
                'info'
            );
        }
    } catch {
        /* Une sonde ne casse jamais l'écran — on préfère perdre la mesure. */
    }
}
